import { useEffect, useRef } from 'react'
import { flutterBridge } from '@/bridge/flutter-bridge'
import { useAuthStore } from '@/store/auth'
import { api } from '@/services/api'

/**
 * Hook to register FCM push token with the backend when authenticated
 * Only active when running inside Flutter WebView
 */
export function usePushToken() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const registeredRef = useRef(false)

  useEffect(() => {
    // Only run in Flutter WebView
    if (!flutterBridge.isInFlutter) return
    // Only run when authenticated
    if (!isAuthenticated) {
      registeredRef.current = false
      return
    }
    // Don't re-register on every render
    if (registeredRef.current) return

    const registerToken = async () => {
      try {
        const token = await flutterBridge.getFcmToken()
        if (token) {
          const appInfo = await flutterBridge.getAppInfo()
          await api.post('/devices', {
            token,
            platform: appInfo.platform,
            app_version: appInfo.version,
          })
          registeredRef.current = true
          console.log('Push token registered successfully')
        }
      } catch (e) {
        console.error('Failed to register push token:', e)
      }
    }

    registerToken()

    // Listen for token updates (e.g., token refresh)
    const unsubscribe = flutterBridge.onEvent('pushTokenUpdated', async (data) => {
      const token = data.token as string
      if (token) {
        try {
          const appInfo = await flutterBridge.getAppInfo()
          await api.post('/devices', {
            token,
            platform: appInfo.platform,
            app_version: appInfo.version,
          })
          console.log('Push token updated successfully')
        } catch (e) {
          console.error('Failed to update push token:', e)
        }
      }
    })

    return () => {
      unsubscribe()
    }
  }, [isAuthenticated])
}
